import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Globe,
  Copy
} from 'lucide-react';

const WithdrawalHistory = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0,
    totalFees: 0,
    totalNetAmount: 0
  });

  useEffect(() => {
    fetchWithdrawalHistory();
  }, []);

  const normalizeStatus = (status) => {
    const value = String(status || '').trim().toLowerCase();
    if (value === 'approved') return 'Approved';
    if (value === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const buildUsersIndex = (usersData = {}) => {
    const index = {};

    Object.entries(usersData || {}).forEach(([dateKey, usersByDate]) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return;
      if (!usersByDate || typeof usersByDate !== 'object') return;

      Object.entries(usersByDate).forEach(([userKey, user]) => {
        if (!user || typeof user !== 'object') return;

        const ipAddress =
          user?.lastLoginLocation?.ip ||
          user?.lastLoginLocation?.ipAddress ||
          user?.ipAddress ||
          user?.lastLoginIP ||
          '';

        const location =
          user?.lastLoginLocation?.country ||
          user?.country ||
          '';

        const userMeta = {
          ipAddress,
          location
        };

        if (user.sponsorId) index[user.sponsorId] = userMeta;
        if (user.userId) index[user.userId] = userMeta;
        index[userKey] = userMeta;
      });
    });

    return index;
  };

  const fetchWithdrawalHistory = async () => {
    try {
      setLoading(true);
      const [withdrawalsSnapshot, usersSnapshot] = await Promise.all([
        get(ref(database, 'withdrawals')),
        get(ref(database, 'users'))
      ]);
      const usersIndex = usersSnapshot.exists() ? buildUsersIndex(usersSnapshot.val()) : {};
      
      if (!withdrawalsSnapshot.exists()) {
        setWithdrawals([]);
        setLoading(false);
        return;
      }

      const allWithdrawals = [];
      const data = withdrawalsSnapshot.val();
      let approved = 0;
      let rejected = 0;
      let totalAmount = 0;
      let totalFees = 0;
      let totalNetAmount = 0;

      // Iterate through date folders
      Object.entries(data).forEach(([date, users]) => {
        Object.entries(users).forEach(([userId, userWithdrawals]) => {
          Object.entries(userWithdrawals).forEach(([withdrawalId, withdrawal]) => {
            // Only show processed withdrawals (not pending)
            const normalizedStatus = normalizeStatus(withdrawal.status);
            if (normalizedStatus !== 'Pending') {
              const userMeta =
                usersIndex[withdrawal.userId] ||
                usersIndex[userId] ||
                usersIndex[withdrawal.sponsorId] ||
                {};

              const withdrawalData = {
                id: withdrawalId,
                date: date,
                userId: withdrawal.userId || userId,
                userName: withdrawal.userName || 'Unknown',
                walletType: withdrawal.walletType || 'working',
                amount: parseFloat(withdrawal.amount) || 0,
                feeAmount: parseFloat(withdrawal.feeAmount) || 0,
                netAmount: parseFloat(withdrawal.netAmount) || 0,
                crypto: withdrawal.crypto || {},
                status: normalizedStatus,
                requestDate: withdrawal.requestDate,
                processedDate: withdrawal.processedDate || withdrawal.requestDate,
                processedBy: withdrawal.processedBy || 'admin',
                withdrawalFee: withdrawal.withdrawalFee || 0,
                ipAddress:
                  withdrawal.ipAddress ||
                  withdrawal.location?.ip ||
                  withdrawal.location?.ipAddress ||
                  userMeta.ipAddress ||
                  'N/A',
                location:
                  withdrawal.location?.country ||
                  withdrawal.country ||
                  userMeta.location ||
                  'N/A'
              };

              allWithdrawals.push(withdrawalData);
              
              totalAmount += withdrawalData.amount;
              totalFees += withdrawalData.feeAmount;
              totalNetAmount += withdrawalData.netAmount;
              
              if (withdrawalData.status === 'Approved') approved++;
              else if (withdrawalData.status === 'Rejected') rejected++;
            }
          });
        });
      });

      // Sort by processed date (newest first)
      allWithdrawals.sort((a, b) => new Date(b.processedDate) - new Date(a.processedDate));

      setWithdrawals(allWithdrawals);
      setStats({
        total: allWithdrawals.length,
        approved,
        rejected,
        totalAmount,
        totalFees,
        totalNetAmount
      });
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Rejected':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Approved':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'Rejected':
        return <XCircle className="h-4 w-4 text-red-400" />;
      default:
        return null;
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const filterData = () => {
    let filtered = withdrawals;
    
    if (statusFilter !== 'All') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }
    
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.processedDate);
        switch(dateFilter) {
          case 'today':
            return itemDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return itemDate > weekAgo;
          case 'month':
            const monthAgo = new Date();
            monthAgo.setDate(monthAgo.getDate() - 30);
            return itemDate > monthAgo;
          default:
            return true;
        }
      });
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.userName?.toLowerCase().includes(term) ||
        item.userId?.toLowerCase().includes(term) ||
        item.crypto?.address?.toLowerCase().includes(term) ||
        item.ipAddress?.toLowerCase().includes(term) ||
        item.location?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredWithdrawals = filterData();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">Withdrawal History</h1>
      <p className="text-gray-400 mb-6">History of all approved and rejected withdrawals</p>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total</div>
          <div className="text-lg font-bold text-white">{stats.total}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Approved</div>
          <div className="text-lg font-bold text-green-400">{stats.approved}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Rejected</div>
          <div className="text-lg font-bold text-red-400">{stats.rejected}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Amount</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalAmount)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Fees</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.totalFees)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Net Amount</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.totalNetAmount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow p-4 mb-6 bg-gray-800/50 rounded-xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user, ID, crypto address, IP or location"
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="All">All Status</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
            
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <button
              onClick={fetchWithdrawalHistory}
              className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Withdrawals Table */}
      <div className="card-glow overflow-hidden mb-6 bg-gray-800/50 rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-3 px-4 text-left text-gray-400">Processed Date</th>
                <th className="py-3 px-4 text-left text-gray-400">User</th>
                <th className="py-3 px-4 text-left text-gray-400">Amount</th>
                <th className="py-3 px-4 text-left text-gray-400">Net Amount</th>
                <th className="py-3 px-4 text-left text-gray-400">Crypto Details</th>
                <th className="py-3 px-4 text-left text-gray-400">Status</th>
                <th className="py-3 px-4 text-left text-gray-400">Processed By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredWithdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="hover:bg-gray-700/20">
                  <td className="py-3 px-4 text-sm text-gray-300">
                    {formatDate(withdrawal.processedDate)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-medium">{withdrawal.userName}</div>
                    <div className="text-xs text-gray-400">{withdrawal.userId}</div>
                    <div className="text-xs text-blue-400">{withdrawal.walletType} Wallet</div>
                    <div className="text-xs text-primary font-mono">{withdrawal.ipAddress}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      {withdrawal.location}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-bold">{formatCurrency(withdrawal.amount)}</div>
                    <div className="text-xs text-gray-400">Fee: {formatCurrency(withdrawal.feeAmount)}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-green-400 font-bold">{formatCurrency(withdrawal.netAmount)}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-white">{withdrawal.crypto?.currency || 'USDT'}</div>
                    <div className="text-xs text-gray-400">{withdrawal.crypto?.network || 'TRC20'}</div>
                    <div className="flex items-start gap-2">
                      <div className="text-xs text-gray-500 break-all" title={withdrawal.crypto?.address}>
                        {withdrawal.crypto?.address || 'N/A'}
                      </div>
                      {withdrawal.crypto?.address && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(withdrawal.crypto.address)}
                          className="text-gray-400 hover:text-white mt-0.5"
                          title="Copy Address"
                          aria-label="Copy Address"
                        >
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(withdrawal.status)}
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${getStatusBadge(withdrawal.status)}`}>
                        {withdrawal.status}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-300">
                    {withdrawal.processedBy || 'admin'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredWithdrawals.length === 0 && (
        <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
          <div className="text-4xl mb-4">📋</div>
          <h3 className="text-lg font-semibold text-white mb-2">No Withdrawal History</h3>
          <p className="text-gray-400">
            {statusFilter === 'All' ? 'No withdrawals processed yet' : `No ${statusFilter} withdrawals found`}
          </p>
        </div>
      )}
    </div>
  );
};

export default WithdrawalHistory;
